/* eslint-disable no-param-reassign */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import {
  BackButton,
  CardManufacturingOrderAnalysis,
  DaySpotAndRangePicker,
  Error,
  Loading,
  StepAnalysisDashboardElements,
  ViewTitle,
} from '@iarc-programing/emanufac-components/src/components';
import { Button, Card, CardContent, TextField } from '@mui/material';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import { config } from '@/configs';
import * as actions from '@/redux/actions';
import { useQuery } from '@/utils/functions';

function DashboardAssigner({ title, subtitle }) {
  const dispatch = useDispatch();
  const browserQuery = useQuery();
  const params = useParams();

  const employee = useSelector((state) => state.employee);
  const manufacturingOrder = useSelector((state) => state.manufacturingOrder);
  const step = useSelector((state) => state.step);
  const stepLog = useSelector((state) => state.stepLog);
  const information = useSelector((state) => state.information);

  // const isFullCalendar = getStorage('full_calendar');
  const [startDate, setStartDate] = useState(
    browserQuery.get('start')
      ? dayjs(browserQuery.get('start')).startOf('month').toISOString()
      : dayjs().startOf('month').toISOString(),
  );
  const [periodText, setPeriodText] = useState('ในเดือนล่าสุด');
  const [endDate, setEndDate] = useState(
    browserQuery.get('end')
      ? dayjs(browserQuery.get('end')).endOf('month').toISOString()
      : dayjs().endOf('month').toISOString(),
  );
  const [isReady, setIsReady] = useState(false);
  const [isUseRange, setIsUseRange] = useState(false);
  const [tempProductCode, setTempProductCode] = useState('');
  const [productCode, setProductCode] = useState('');

  // console.log('isFullCalendar', isFullCalendar);

  useEffect(() => {
    const fetchManufacturingOrder = async () => {
      try {
        setIsReady(false);
        await dispatch(
          actions.manufacturingOrderAll({
            all: true,
            startDate,
            endDate,
            analyzed: true,
            page: 1,
            productCode,
            fetchProduct: productCode === '' ? '' : true,
            assigner: params.id,
          }),
        );
        setIsReady(true);
      } catch (error) {
        console.error(error);
      }
    };

    const fetchStep = async () => {
      try {
        await dispatch(
          actions.stepAll({
            all: true,
            startDate: dayjs(startDate).format('YYYY-MM-DD'),
            endDate: dayjs(endDate).format('YYYY-MM-DD'),
            size: config.maxFetchSize,
            productCode,
            assigner: params.id,
          }),
        );
        await dispatch(
          actions.stepLogAll({
            all: true,
            startDate: dayjs(startDate).format('YYYY-MM-DD'),
            endDate: dayjs(endDate).format('YYYY-MM-DD'),
            size: config.maxFetchSize,
            productCode,
            assigner: params.id,
          }),
        );
      } catch (error) {
        console.error(error);
      }
    };

    // const checkFullCalendar = async () => {
    //   if (!isFullCalendar) {
    //     await setStorage('full_calendar', true);
    //   }
    // };

    fetchManufacturingOrder();
    fetchStep();

    return () => {};
  }, [startDate, endDate, productCode]);

  const renderTitle = () => (
    <ViewTitle
      title={`${title} ${
        employee?.display_name ||
        `${employee?.firstname} ${employee?.lastname || ''}`
      }`}
      subtitle={subtitle}
    />
  );

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setProductCode(tempProductCode);
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [tempProductCode]);

  useEffect(() => {
    dispatch(actions.employeeGet(params.id));

    return () => {};
  }, [params.id]);

  if (step.isLoading || manufacturingOrder.isLoading) {
    return <Loading />;
  }
  if (step.isCompleted && manufacturingOrder.isCompleted) {
    return (
      <div>
        {renderTitle()}
        <div className="flex justify-between my-2">
          <BackButton />
          <div className="flex gap-2">
            <div className="flex gap-1">
              <Link to="/mms/dashboard/other">
                <Button variant="contained">รายงานอื่นๆ</Button>
              </Link>{' '}
            </div>
          </div>
        </div>
        <div className="my-2 flex flex-wrap">
          <div className="w-full lg:w-3/3 ">
            <CardManufacturingOrderAnalysis
              manufacturingOrder={manufacturingOrder}
              selectedMonthDate={
                periodText === 'ในเดือนล่าสุด' ? startDate : null
              }
              showRevenue={false}
              showProductionResult={false}
              showMoreInfoBox
              isReady={isReady}
              isUseRange={isUseRange}
              endDate={endDate}
              productCode={productCode}
              assigner={params.id}
            />
          </div>
          <div className="w-full my-1">
            <Card>
              <CardContent>
                <DaySpotAndRangePicker
                  endDate={endDate}
                  setEndDate={setEndDate}
                  startDate={startDate}
                  setStartDate={setStartDate}
                  state="month"
                  flex
                  setParentUseRange={setIsUseRange}
                  addSearchQuery
                  enable
                  label="เลือกเดือนที่ต้องการ"
                  anotherQuery="?"
                  showTypeSelector
                />
                {/** TODO: Option It */}
                <div>ค้นหาจากรหัสสินค้า</div>
                <div className="w-full lg:w-1/4 md:w-1/2">
                  <TextField
                    size="small"
                    label="รหัสสินค้า หลายรหัสขั้นด้วย ,"
                    fullWidth
                    onChange={(e) => setTempProductCode(e.target.value)}
                    value={tempProductCode}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="w-full  my-2">
            <StepAnalysisDashboardElements
              step={step}
              stepLog={stepLog}
              isReady={isReady}
              manufacturingOrder={manufacturingOrder}
              startDate={startDate}
              endDate={endDate}
              showClaimedWork={information?.setting?.mms?.enableClaimedMO}
              assigner={params.id}
            />
          </div>
        </div>
      </div>
    );
  }
  return <Error />;
}
DashboardAssigner.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
};

DashboardAssigner.defaultProps = {
  title: '',
  subtitle: '',
};

export default DashboardAssigner;
